import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Download, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";

const MAX = 5;

export function ImageViewer({ src, onClose }) {
  const [v, setV] = useState({ s: 1, x: 0, y: 0 });
  const box = useRef(null);
  const g = useRef(null);
  const lastTap = useRef(0);

  // Zoom around a screen point (px, py) so that point stays under the finger
  const zoomAt = (cur, ns, px, py) => {
    const s = Math.min(MAX, Math.max(1, ns));
    if (s === 1) return { s: 1, x: 0, y: 0 };
    const r = box.current.getBoundingClientRect();
    const cx = px - r.left - r.width / 2, cy = py - r.top - r.height / 2;
    const k = s / cur.s;
    return { s, x: cx - (cx - cur.x) * k, y: cy - (cy - cur.y) * k };
  };
  const mid = (t) => [(t[0].clientX + t[1].clientX) / 2, (t[0].clientY + t[1].clientY) / 2];
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  const onTouchStart = (e) => {
    const t = e.touches;
    if (t.length === 2) { g.current = { type: "pinch", d: dist(t), m: mid(t), v }; return; }
    const now = Date.now();
    if (now - lastTap.current < 300) { setV((c) => (c.s > 1 ? { s: 1, x: 0, y: 0 } : zoomAt(c, 2.5, t[0].clientX, t[0].clientY))); lastTap.current = 0; return; }
    lastTap.current = now;
    g.current = { type: "pan", p: [t[0].clientX, t[0].clientY], v };
  };
  const onTouchMove = (e) => {
    const t = e.touches, st = g.current;
    if (!st) return;
    if (st.type === "pinch" && t.length === 2) {
      const [mx, my] = mid(t);
      const z = zoomAt(st.v, st.v.s * (dist(t) / st.d), st.m[0], st.m[1]);
      setV(z.s === 1 ? z : { ...z, x: z.x + mx - st.m[0], y: z.y + my - st.m[1] });
    } else if (st.type === "pan" && t.length === 1 && st.v.s > 1) {
      setV({ ...st.v, x: st.v.x + t[0].clientX - st.p[0], y: st.v.y + t[0].clientY - st.p[1] });
    }
  };
  const onWheel = (e) => setV((c) => zoomAt(c, c.s * (e.deltaY < 0 ? 1.2 : 1 / 1.2), e.clientX, e.clientY));
  const onMouseDown = (e) => { if (v.s > 1) g.current = { type: "pan", p: [e.clientX, e.clientY], v }; };
  const onMouseMove = (e) => { const st = g.current; if (st && st.type === "pan") setV({ ...st.v, x: st.v.x + e.clientX - st.p[0], y: st.v.y + e.clientY - st.p[1] }); };
  const btnZoom = (f) => { const r = box.current.getBoundingClientRect(); setV((c) => zoomAt(c, c.s * f, r.left + r.width / 2, r.top + r.height / 2)); };

  const save = async () => {
    try {
      const blob = await fetch(src).then((r) => r.blob());
      const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const file = new File([blob], `gjejshqip-${Date.now()}.${ext}`, { type: blob.type });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(file);
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success("Fotoja u ruajt");
    } catch { toast.error("Fotoja nuk u ruajt"); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[2000] bg-black flex flex-col" data-testid="image-viewer">
      <div className="flex items-center justify-between p-3 text-white relative z-10">
        <button onClick={onClose} data-testid="image-viewer-close" aria-label="Mbyll" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
        <div className="flex gap-2">
          <button onClick={() => btnZoom(1 / 1.5)} data-testid="image-viewer-zoom-out" aria-label="Zvogëlo" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><ZoomOut className="w-5 h-5" /></button>
          <button onClick={() => btnZoom(1.5)} data-testid="image-viewer-zoom-in" aria-label="Zmadho" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><ZoomIn className="w-5 h-5" /></button>
          <button onClick={save} data-testid="image-viewer-save" className="h-10 px-4 rounded-full bg-emerald-500 flex items-center gap-1.5 text-sm font-semibold"><Download className="w-4 h-4" />Ruaj</button>
        </div>
      </div>
      <div ref={box} className="flex-1 min-h-0 overflow-hidden touch-none select-none flex items-center justify-center"
        onClick={(e) => { if (v.s === 1 && e.target === e.currentTarget) onClose(); }}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={(e) => { if (!e.touches.length) g.current = null; }}
        onWheel={onWheel} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={() => { g.current = null; }} onMouseLeave={() => { g.current = null; }}>
        <img src={src} alt="" data-testid="image-viewer-img" draggable={false}
          className="max-w-full max-h-full object-contain will-change-transform"
          style={{ transform: `translate(${v.x}px, ${v.y}px) scale(${v.s})`, cursor: v.s > 1 ? "grab" : "zoom-in" }} />
      </div>
    </div>,
    document.body
  );
}
